import { useMemo, useState } from "react";
import { Button, Select, Tag, Tooltip } from "antd";
import { ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import CommonTable from "../../components/commonTable";
import CustomPagination from "../../components/pagination";
import DateRangeFilter from "../../components/dateRangeFilter";
import AddVendorTransactionModal from "../../components/modals/AddVendorTransactionModal";
import { useGetVendorTransactionsQuery, useGetMerchantTransactionsQuery } from "../../services/vendor";
import usePermissions from "../../hooks/usePermissions";
import "./styles.scss";

interface DateRangeValue { start: Date; end: Date; }

const paidViaOptions = [
  { value: 'all',         label: 'All Paid Via' },
  { value: 'wallet',      label: 'Wallet'       },
  { value: 'outstanding', label: 'Outstanding'  },
];

const paymentTypeOptions = [
  { value: 'all',     label: 'All Payment Types' },
  { value: 'full',    label: 'Full'    },
  { value: 'partial', label: 'Partial' },
];

/* ─── Types ──────────────────────────────────────────────────────────── */

interface TransactionRecord {
  key:          string;
  dateTime?:    string;
  merchantName?: string;
  description?: string;
  amount?:      number;
  paidAmount?:  number;
  dueAmount?:   number;
  paymentType?: string;
  paidVia?:     string;
  paymentMode?: string;
  paymentBill?: string;
  bill?:        string;
  category?:    string;
  createdBy?:   string;
  branchNames?: string;
}

/* ─── Component ──────────────────────────────────────────────────────── */

const renderDateTime = (dateTime?: string) => {
  if (!dateTime) return '-';
  const spaceIdx = dateTime.indexOf(' ');
  const datePart = spaceIdx === -1 ? dateTime : dateTime.slice(0, spaceIdx);
  const timePart = spaceIdx === -1 ? '' : dateTime.slice(spaceIdx + 1);
  const dt = new Date(datePart);
  const dateLabel = isNaN(dt.getTime())
    ? datePart
    : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  return (
    <div>
      <div>{dateLabel}</div>
      {timePart && <div style={{ fontSize: 12, opacity: 0.65 }}>{timePart}</div>}
    </div>
  );
};

const AMOUNT_COLOR = '#52c41a';
const DUE_COLOR    = '#ff4d4f';

const renderAmount = (v?: number, color?: string) =>
  v === undefined || v === null ? '-' : <span style={{ color }}>₹{v.toLocaleString('en-IN')}</span>;

const VendorTransactions = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const vendorId   = searchParams.get('vendorId') || undefined;
  const merchantId = searchParams.get('merchantId') || undefined;
  const entityName = (location.state as { vendorName?: string; entityName?: string } | null)?.entityName
    ?? (location.state as { vendorName?: string } | null)?.vendorName;
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [page,        setPage]        = useState(1);
  const [limit,       setLimit]       = useState(10);
  const [paidVia,     setPaidVia]     = useState("all");
  const [paymentType, setPaymentType] = useState("all");
  const [dateRange,   setDateRange]   = useState<DateRangeValue | null>(null);

  const { data: vendorTransactionsResp, isFetching: vendorFetching } = useGetVendorTransactionsQuery({
    vendorId:    vendorId as string,
    paidVia:     paidVia     !== 'all' ? (paidVia as 'wallet' | 'outstanding') : undefined,
    paymentType: paymentType !== 'all' ? (paymentType as 'full' | 'partial')   : undefined,
    page,
    limit,
  }, { skip: !vendorId });

  const { data: merchantTransactionsResp, isFetching: merchantFetching } = useGetMerchantTransactionsQuery({
    merchantId:  merchantId as string,
    paidVia:     paidVia     !== 'all' ? (paidVia as 'wallet' | 'outstanding') : undefined,
    paymentType: paymentType !== 'all' ? (paymentType as 'full' | 'partial')   : undefined,
    startDate:   dateRange?.start ? dayjs(dateRange.start).format('YYYY-MM-DD') : undefined,
    endDate:     dateRange?.end   ? dayjs(dateRange.end).format('YYYY-MM-DD')   : undefined,
    page,
    limit,
  }, { skip: !merchantId });

  const isFetching = vendorId ? vendorFetching : merchantFetching;
  const apiData = (vendorId ? vendorTransactionsResp : merchantTransactionsResp) as any;

  const transactions = useMemo<TransactionRecord[]>(() => {
    const rows: any[] = apiData?.data || [];
    return rows.map((t: any) => ({
      key:          t._id,
      dateTime:     t.transactionDateFormatted || t.transactionDate || t.dateTime || t.createdAt,
      merchantName: t.merchantId?.name || t.vendor?.name,
      description:  t.description,
      amount:       t.amount,
      paidAmount:   t.paidAmount,
      dueAmount:    t.dueAmount,
      paymentType:  t.paymentType,
      paidVia:      t.paidVia,
      paymentMode:  t.paymentMode,
      paymentBill:  t.paymentBill,
      bill:         t.bill,
      category:     t.merchantId?.category || t.vendor?.category,
      createdBy:    t.createdBy?.name,
      branchNames:  Array.isArray(t.branchIds)
        ? t.branchIds.map((b: any) => b?.name).filter(Boolean).join(', ')
        : (t.branch?.name || t.branchId?.name),
    }));
  }, [apiData]);

  const columns = [
    { title: 'Date',        dataIndex: 'dateTime',    key: 'dateTime',    render: renderDateTime },
    { title: 'Merchant',    dataIndex: 'merchantName', key: 'merchantName', render: (v?: string) => v || '-' },
    { title: 'Description', dataIndex: 'description', key: 'description', render: (v?: string) => v || '-' },
    { title: 'Amount',      dataIndex: 'amount',      key: 'amount',      render: (v?: number) => renderAmount(v) },
    { title: 'Paid',        dataIndex: 'paidAmount',  key: 'paidAmount',  render: (v?: number) => renderAmount(v, AMOUNT_COLOR) },
    { title: 'Due',         dataIndex: 'dueAmount',   key: 'dueAmount',   render: (v?: number) => renderAmount(v, v ? DUE_COLOR : undefined) },
    { title: 'Payment Type', dataIndex: 'paymentType', key: 'paymentType', align: 'center' as const,
      render: (v?: string) => v ? <Tag color={v === 'full' ? 'green' : 'orange'}>{v.toUpperCase()}</Tag> : '-' },
    { title: 'Paid Via',     dataIndex: 'paidVia',     key: 'paidVia',     align: 'center' as const,
      render: (v?: string) => v ? <Tag color={v === 'wallet' ? 'blue' : 'default'}>{v.toUpperCase()}</Tag> : '-' },
    { title: 'Payment Mode', dataIndex: 'paymentMode', key: 'paymentMode', align: 'center' as const,
      render: (v?: string, record?: TransactionRecord) => {
        if (!v) return '-';
        const tag = <Tag color={v === 'online' ? 'purple' : 'default'}>{v.toUpperCase()}</Tag>;
        if (v !== 'online' || !record?.paymentBill) return tag;
        return (
          <Tooltip
            title={
              <div style={{ textAlign: 'center' }}>
                <img src={record.paymentBill} alt="Payment proof" style={{ maxWidth: 160, maxHeight: 160, borderRadius: 6, display: 'block', marginBottom: 6 }} />
                <a href={record.paymentBill} target="_blank" rel="noreferrer">View</a>
              </div>
            }
          >
            {tag}
          </Tooltip>
        );
      } },
    { title: 'Bill',         dataIndex: 'bill',        key: 'bill',        align: 'center' as const,
      render: (v?: string) => v ? <a href={v} target="_blank" rel="noreferrer">View</a> : '-' },
    { title: 'Category',    dataIndex: 'category',    key: 'category',    render: (v?: string) => v || '-' },
    { title: 'Employee',    dataIndex: 'createdBy',   key: 'createdBy',   render: (v?: string) => v || '-' },
    { title: 'Branch',       dataIndex: 'branchNames', key: 'branchNames', render: (v?: string) => v || '-' },
  ];

  return (
    <div className="vendor-transactions-page">
      <div className="header-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />
          <h2 style={{ margin: 0 }}>{entityName || (merchantId ? 'Merchant' : 'Vendor')} Transactions</h2>
        </div>
        {hasPermission(merchantId ? 'MERCHANT_CREATE' : 'VENDOR_CREATE') && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
            Add Transaction
          </Button>
        )}
      </div>

      <div className="filters-section">
        <Select
          value={paidVia}
          onChange={(v: string) => { setPaidVia(v); setPage(1); }}
          style={{ width: 180, height: 41 }}
          options={paidViaOptions}
        />
        <Select
          value={paymentType}
          onChange={(v: string) => { setPaymentType(v); setPage(1); }}
          style={{ width: 190, height: 41 }}
          options={paymentTypeOptions}
        />
        {merchantId && (
          <DateRangeFilter
            value={dateRange}
            onChange={(v) => { setDateRange(v); setPage(1); }}
          />
        )}
      </div>

      <div className="vendor-transactions-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={transactions}
          loading={isFetching}
          pagination={false}
          rowKey={(record: TransactionRecord) => record.key}
          scroll={{ x: 1600 }}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={apiData?.total || 0}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />

      <AddVendorTransactionModal
        visible={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        vendorId={vendorId}
        merchantId={merchantId}
      />
    </div>
  );
};

export default VendorTransactions;
